import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Bible.module.scss";
import { IRootState } from "../../../../../redux/store";
import { useTranslation } from "react-i18next";

interface Props {
	setIsSaveDisabled: (value: boolean) => void;
	setPromptText: (value: string) => void;
};

const MAX_CHAR_LIMIT = 3000;
const MIN_CHAR_LIMIT = 50;
const MAX_HEIGHT = 515;
const OFFSET = 15;

export const Bible: React.FC<Props> = ({ setIsSaveDisabled, setPromptText }) => {
	const { t } = useTranslation('form');
	const formData = useSelector((state: IRootState) => state.avatarForm);
	const { gender, promptKey, enName } = useSelector((state: IRootState) => state.avatar);
	const genderPropmt = gender === "male" ? "male_prompt" : "female_prompt";
	const defaultText = t(promptKey || genderPropmt, { name: t(enName) });
	const OVER_LIMIT_TEXT = t('char_limit_exceeded', { limit: MAX_CHAR_LIMIT });
	const MIN_LIMIT_TEXT = t('char_limit', { limit: MIN_CHAR_LIMIT });
	const [textareaValue, setTextareaValue] = useState<string>(formData.prompt || defaultText);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const isOverLimit = textareaValue.length >= (MAX_CHAR_LIMIT);
	const isUnderLimit = textareaValue.length < (MIN_CHAR_LIMIT);
	const isError = isOverLimit || isUnderLimit;
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!textareaRef.current) return;
		const textarea = textareaRef.current;
		textarea.style.height = "auto"; 
		const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
		textarea.style.height = `${newHeight + OFFSET}px`;
	}, [textareaValue]);

	useEffect(() => {
		if (formData.prompt) {
			setTextareaValue(formData.prompt);
		}
	}, [formData.prompt]);

	useEffect(() => {
		isError ? setIsSaveDisabled(true) : setIsSaveDisabled(false);
	}, [isError]);

	const handleBlur = useCallback(() => {
		setIsEditing(false);
	}, []);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newValue = e.target.value;
		setTextareaValue(newValue);
		setPromptText(newValue);
	};

	return (
		<div className={styles.bibleContainer}>
			<div className={styles.textContainer}>
				<h5>{t("add_prompt")}</h5>
				<span>{t("describe_prompt")}</span>
			</div>
			<textarea
				ref={textareaRef}
				value={textareaValue}
				onChange={handleChange}
				onBlur={handleBlur}
				onFocus={() => setIsEditing(true)}
				className={`${isError ? styles.overLimit : ""} ${isEditing ? styles.editing : ""}`}
				spellCheck={false}
			/>
			<span
				className={`${styles.description} ${isError ? styles.errorText : ""}`}
			>
				{isUnderLimit && MIN_LIMIT_TEXT}
				{isOverLimit && OVER_LIMIT_TEXT}
			</span>
		</div>
	);
};
